import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { ZmanimWidget } from '../../../business/ZmanimWidget/ZmanimWidget';
import { dummyZmanimData } from '../../../dummies/WidgetPreview/dummyData';
import { PREVIEW_W, PREVIEW_H } from '../components/HeroPreviewPage';

const easeOut = (t) => 1 - Math.pow(1 - t, 3);
const noopDispatch = () => {};

// ── Screen geometry ────────────────────────────────────────────────────────
const SCREEN_W         = 1060;
const SCREEN_BAR_H     = 44;
const SCREEN_CONTENT_H = 623;  // maintains 1600:940 aspect ratio
const SCREEN_STAND_H   = 24;
const SCREEN_TOTAL_H   = SCREEN_BAR_H + SCREEN_CONTENT_H + SCREEN_STAND_H;

// iframe is rendered at PREVIEW_W×PREVIEW_H then CSS-scaled to fit SCREEN_W
const IFRAME_SCALE = SCREEN_W / PREVIEW_W; // 1060/1600 = 0.6625

// ── Dashboard screen ───────────────────────────────────────────────────────
const HeroScreen = ({ iframeRef }) => (
  <Box sx={{ width: SCREEN_W, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

    {/* TV bezel — thin black frame */}
    <Box sx={{
      border: '10px solid #111',
      borderRadius: '10px',
      boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.06)',
      overflow: 'hidden',
      bgcolor: '#111',
      width: '100%',
    }}>
      {/* Live indicator bar — very slim */}
      <Box sx={{
        height: 28, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 1.5, bgcolor: '#0a0a0a',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{
            width: 7, height: 7, borderRadius: '50%', bgcolor: '#28c840',
            boxShadow: '0 0 6px #28c840',
            animation: 'pulse 1.8s ease-in-out infinite',
          }} />
          <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.55rem', fontFamily: 'Rubik, sans-serif', letterSpacing: '0.08em' }}>
            LIVE
          </Typography>
        </Box>
        <Typography sx={{ color: 'rgba(255,255,255,0.15)', fontSize: '0.55rem', fontFamily: 'Rubik, sans-serif' }}>
          inorai.app
        </Typography>
        <Box sx={{ width: 28 }} />
      </Box>

      {/* Live iframe */}
      <Box sx={{ height: SCREEN_CONTENT_H, overflow: 'hidden', position: 'relative', bgcolor: '#f0f3f7' }}>
        <iframe
          ref={iframeRef}
          src="/_preview"
          scrolling="no"
          title="תצוגה מקדימה"
          style={{
            position:        'absolute',
            top:             0,
            left:            0,
            width:           PREVIEW_W,
            height:          PREVIEW_H,
            border:          'none',
            transform:       `scale(${IFRAME_SCALE})`,
            transformOrigin: 'top left',
            pointerEvents:   'auto',
          }}
        />
      </Box>
    </Box>

  </Box>
);

/* ── Main Hero ── */
const HeroSection = () => {
  const navigate = useNavigate();
  const wrapperRef = useRef(null);

  // Refs for direct DOM mutation — no React re-renders on scroll
  const panelRef           = useRef(null);
  const screenContainerRef = useRef(null);
  const screen3DRef        = useRef(null);
  const textBoxRef         = useRef(null);
  const scrollHintRef      = useRef(null);
  const tryItRef           = useRef(null);
  const bottomCalloutRef   = useRef(null);
  const bgGlowRef          = useRef(null);
  const maxScaleRef        = useRef(0.88);
  const iframeRef          = useRef(null);
  const onboardingSentRef  = useRef(false);
  const glowRef            = useRef(null);
  const panelPosRef        = useRef('fixed');

  useEffect(() => {
    const compute = () => {
      // 70 = navbar, 60 = top callout, 110 = bottom callout + stand
      const byH = Math.min(1.0, (window.innerHeight - 240) / SCREEN_TOTAL_H);
      const byW = Math.min(1.0, (window.innerWidth  - 80) / SCREEN_W);
      maxScaleRef.current = Math.min(byH, byW);
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);

  useEffect(() => {
    let rafId = null;

    const update = () => {
      rafId = null;
      const wrapper = wrapperRef.current;
      if (!wrapper) return;

      const scrolled    = -wrapper.getBoundingClientRect().top;
      const growRange   = window.innerHeight * 0.75;
      const totalActive = window.innerHeight * 0.85;
      const p           = easeOut(Math.min(1, Math.max(0, scrolled / growRange)));
      const ms          = maxScaleRef.current;

      // panel: fixed while animating, absolute once done — only mutate on state change to avoid layout reflow
      const panel = panelRef.current;
      if (panel) {
        const newPos = scrolled < totalActive ? 'fixed' : 'absolute';
        if (newPos !== panelPosRef.current) {
          panelPosRef.current = newPos;
          if (newPos === 'fixed') {
            panel.style.position = 'fixed';
            panel.style.top      = '0';
            panel.style.bottom   = 'auto';
          } else {
            panel.style.position = 'absolute';
            panel.style.top      = 'auto';
            panel.style.bottom   = '0';
          }
        }
      }

      const sc = screenContainerRef.current;
      if (sc) {
        // translate3d forces GPU composite layer — smoother on non-Mac hardware
        sc.style.transform = `translate3d(-50%, -50%, 0) scale(${0.34 + (ms - 0.34) * p})`;
        sc.style.opacity   = 0.38 + 0.62 * p;
      }

      const s3 = screen3DRef.current;
      if (s3) {
        // transform only — filter is static in JSX, outline replaced by glowRef opacity
        s3.style.transform = `perspective(1400px) rotateY(${-14 * (1 - p)}deg) rotateX(${5 * (1 - p)}deg)`;
      }
      if (glowRef.current) {
        glowRef.current.style.opacity = p > 0.7
          ? Math.min(0.7, (p - 0.7) / 0.3 * 0.7)
          : 0;
      }

      if (textBoxRef.current) {
        const textOpacity = Math.max(0, 1 - p * 1.1);
        textBoxRef.current.style.opacity       = textOpacity;
        textBoxRef.current.style.transform     = `translateY(${-p * 50}px)`;
        textBoxRef.current.style.pointerEvents = textOpacity === 0 ? 'none' : 'auto';
      }
      if (scrollHintRef.current)
        scrollHintRef.current.style.opacity = Math.max(0, 1 - p * 5);
      if (tryItRef.current)
        tryItRef.current.style.opacity = Math.max(0, (p - 0.55) / 0.35);
      if (bottomCalloutRef.current)
        bottomCalloutRef.current.style.opacity = Math.max(0, (p - 0.75) / 0.25);
      // opacity-only update — no gradient repaint per frame
      if (bgGlowRef.current)
        bgGlowRef.current.style.opacity = 0.04 + 0.14 * p;

      // Notify iframe when surrounding texts appear — show onboarding entry card
      if (p >= 0.72 && !onboardingSentRef.current) {
        onboardingSentRef.current = true;
        iframeRef.current?.contentWindow?.postMessage({ type: 'INORAI_SCREEN_EXPANDED' }, '*');
      }
    };

    const onScroll = () => {
      if (!rafId) rafId = requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  const scrollToDemo = () => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const target = wrapper.getBoundingClientRect().top + window.scrollY + window.innerHeight * 0.75;
    const start = window.scrollY;
    const distance = target - start;
    const duration = 1800;
    const startTime = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - startTime) / duration);
      const ease = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
      window.scrollTo(0, start + distance * ease);
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  return (
    <Box ref={wrapperRef} sx={{ height: '185vh', position: 'relative' }}>

      <Box ref={panelRef}
        sx={{ height: '100vh', bgcolor: '#1e3a5f', overflow: 'hidden', left: 0, right: 0, zIndex: 50 }}
        style={{ position: 'fixed', top: 0 }}
      >

        {/* ── Background ── */}

        {/* Base dot grid — static, very subtle */}
        <Box sx={{
          position: 'absolute', inset: 0,
          backgroundImage: 'radial-gradient(rgba(218,165,32,0.06) 1px, transparent 1px)',
          backgroundSize: '28px 28px', pointerEvents: 'none',
        }} />
        {/* Gold sparkle — group A */}
        <Box sx={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: 'radial-gradient(rgba(218,165,32,0.22) 1.5px, transparent 1.5px)',
          backgroundSize: '112px 112px', backgroundPosition: '0 0',
          animation: 'heroDotTwinkle 2.9s ease-in-out infinite',
        }} />
        {/* Gold sparkle — group B (offset) */}
        <Box sx={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: 'radial-gradient(rgba(200,155,20,0.16) 1.5px, transparent 1.5px)',
          backgroundSize: '84px 84px', backgroundPosition: '14px 28px',
          animation: 'heroDotTwinkle 3.7s ease-in-out 1.3s infinite',
        }} />
        {/* Gold sparkle — group C (slower) */}
        <Box sx={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: 'radial-gradient(rgba(255,210,60,0.25) 1.5px, transparent 1.5px)',
          backgroundSize: '140px 112px', backgroundPosition: '42px 14px',
          animation: 'heroDotTwinkle 4.4s ease-in-out 2.5s infinite',
        }} />
        {/* Gold sparkle — group D (extra stagger) */}
        <Box sx={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: 'radial-gradient(rgba(218,165,32,0.18) 1px, transparent 1px)',
          backgroundSize: '196px 140px', backgroundPosition: '70px 56px',
          animation: 'heroDotTwinkle 5.1s ease-in-out 0.7s infinite',
        }} />
        {/* Gold sparkle — group E (fine grid, quickest) */}
        <Box sx={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: 'radial-gradient(rgba(255,200,30,0.14) 1px, transparent 1px)',
          backgroundSize: '168px 196px', backgroundPosition: '28px 84px',
          animation: 'heroDotTwinkle 3.3s ease-in-out 2.1s infinite',
        }} />

        <Box sx={{
          position: 'absolute', top: 0, right: 0, width: '55vw', height: '70vh',
          background: 'radial-gradient(ellipse at top right, rgba(154,114,9,0.2) 0%, transparent 60%)',
          pointerEvents: 'none',
        }} />

        {/* Large gold circle — border-glow comet CW */}
        <Box sx={{
          position: 'absolute', right: '-14%', top: '50%',
          transform: 'translateY(-50%)', width: '74vw', height: '74vw',
          borderRadius: '50%', border: '1px solid rgba(154,114,9,0.22)', pointerEvents: 'none',
        }}>
          <Box sx={{
            position: 'absolute', inset: 0, borderRadius: '50%',
            background: 'conic-gradient(from 0deg, transparent 93%, rgba(154,114,9,0.1) 95%, rgba(218,165,32,0.55) 96.5%, rgba(255,230,80,0.95) 97.5%, rgba(218,165,32,0.55) 98.5%, rgba(154,114,9,0.1) 99.5%, transparent 100%)',
            WebkitMask: 'radial-gradient(circle closest-side at 50% 50%, transparent 97.5%, rgba(0,0,0,0.5) 98.5%, black 99.2%, black 100.2%, rgba(0,0,0,0.3) 101%)',
            mask: 'radial-gradient(circle closest-side at 50% 50%, transparent 97.5%, rgba(0,0,0,0.5) 98.5%, black 99.2%, black 100.2%, rgba(0,0,0,0.3) 101%)',
            animation: 'spinCW 22s linear infinite',
          }} />
        </Box>

        {/* Medium gold circle — border-glow comet CCW */}
        <Box sx={{
          position: 'absolute', right: '-7%', top: '50%',
          transform: 'translateY(-50%)', width: '50vw', height: '50vw',
          borderRadius: '50%', border: '1px solid rgba(154,114,9,0.16)', pointerEvents: 'none',
        }}>
          <Box sx={{
            position: 'absolute', inset: 0, borderRadius: '50%',
            background: 'conic-gradient(from 180deg, transparent 93%, rgba(154,114,9,0.08) 95%, rgba(218,165,32,0.45) 96.5%, rgba(255,230,80,0.82) 97.5%, rgba(218,165,32,0.45) 98.5%, rgba(154,114,9,0.08) 99.5%, transparent 100%)',
            WebkitMask: 'radial-gradient(circle closest-side at 50% 50%, transparent 97.5%, rgba(0,0,0,0.5) 98.5%, black 99.2%, black 100.2%, rgba(0,0,0,0.3) 101%)',
            mask: 'radial-gradient(circle closest-side at 50% 50%, transparent 97.5%, rgba(0,0,0,0.5) 98.5%, black 99.2%, black 100.2%, rgba(0,0,0,0.3) 101%)',
            animation: 'spinCCW 34s linear infinite',
          }} />
        </Box>

        {/* Small circle (bottom-left) — border-glow comet CW */}
        <Box sx={{
          position: 'absolute', left: '-8%', bottom: '-12%',
          width: '35vw', height: '35vw',
          borderRadius: '50%', border: '1px solid rgba(154,114,9,0.13)', pointerEvents: 'none',
        }}>
          <Box sx={{
            position: 'absolute', inset: 0, borderRadius: '50%',
            background: 'conic-gradient(from 270deg, transparent 93%, rgba(154,114,9,0.08) 95%, rgba(218,165,32,0.4) 96.5%, rgba(255,230,80,0.7) 97.5%, rgba(218,165,32,0.4) 98.5%, rgba(154,114,9,0.08) 99.5%, transparent 100%)',
            WebkitMask: 'radial-gradient(circle closest-side at 50% 50%, transparent 97.5%, rgba(0,0,0,0.5) 98.5%, black 99.2%, black 100.2%, rgba(0,0,0,0.3) 101%)',
            mask: 'radial-gradient(circle closest-side at 50% 50%, transparent 97.5%, rgba(0,0,0,0.5) 98.5%, black 99.2%, black 100.2%, rgba(0,0,0,0.3) 101%)',
            animation: 'spinCW 28s linear infinite',
          }} />
        </Box>
        <Box
          ref={bgGlowRef}
          sx={{ position: 'absolute', left: '50%', top: '50%', width: '65vw', height: '65vh', borderRadius: '50%', pointerEvents: 'none', willChange: 'opacity' }}
          style={{ transform: 'translate(-50%, -50%)', background: 'radial-gradient(ellipse, rgba(154,114,9,1) 0%, transparent 65%)', opacity: 0.04 }}
        />

        {/* ── SCREEN — always centered, grows in place ── */}
        <Box
          ref={screenContainerRef}
          sx={{ position: 'absolute', left: '50%', top: 'calc(50% + 30px)', zIndex: 2, display: { xs: 'none', md: 'block' } }}
          style={{
            transform: 'translate(-50%, -50%) scale(0.34)',
            transformOrigin: 'center center',
            opacity: 0.38,
            willChange: 'transform, opacity',
          }}
        >
          {/* ── "Try it" callout — appears above screen as it grows ── */}
          <Box
            ref={tryItRef}
            style={{ opacity: 0 }}
            sx={{ mb: 1.5, textAlign: 'center' }}
          >
            <Typography sx={{
              fontFamily: 'Rubik, sans-serif', fontWeight: 900,
              fontSize: '1.35rem', letterSpacing: '-0.01em',
              background: 'linear-gradient(90deg, #d4a017, #f0c040, #d4a017)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              backgroundSize: '200% auto',
              animation: 'goldSweep 2.5s linear infinite',
            }}>
              ← גרור רכיבים · שנה גדלים · הכל חי ממש כאן →
            </Typography>
          </Box>

          {/* Screen with glowing interactive border */}
          <Box ref={screen3DRef} style={{
            transform: 'perspective(1400px) rotateY(-14deg) rotateX(5deg)',
            filter: 'drop-shadow(0 30px 60px rgba(0,0,0,0.55)) drop-shadow(0 0 30px rgba(154,114,9,0.22))',
            willChange: 'transform',
            borderRadius: '13px',
            position: 'relative',
          }}>
            <HeroScreen iframeRef={iframeRef} />
            {/* Gold outline glow — animated via opacity only, no repaint */}
            <Box ref={glowRef} style={{ opacity: 0 }} sx={{
              position: 'absolute', inset: -3,
              borderRadius: '16px',
              boxShadow: '0 0 0 2px rgba(154,114,9,0.7)',
              pointerEvents: 'none',
              willChange: 'opacity',
            }} />
          </Box>

          {/* ── Bottom callout — fades in last ── */}
          <Box
            ref={bottomCalloutRef}
            style={{ opacity: 0 }}
            sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.25 }}
          >
            {/* Live dot + headline */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Box sx={{
                width: 10, height: 10, borderRadius: '50%', bgcolor: '#28c840',
                boxShadow: '0 0 0 4px rgba(40,200,64,0.2)',
                animation: 'pulse 1.8s ease-in-out infinite', flexShrink: 0,
              }} />
              <Typography sx={{
                color: 'white', fontFamily: 'Rubik, sans-serif',
                fontWeight: 800, fontSize: '1.2rem',
              }}>
                זה לא צילום מסך — תנסה בעצמך!
              </Typography>
              <Box sx={{
                width: 10, height: 10, borderRadius: '50%', bgcolor: '#28c840',
                boxShadow: '0 0 0 4px rgba(40,200,64,0.2)',
                animation: 'pulse 1.8s ease-in-out infinite 0.9s', flexShrink: 0,
              }} />
            </Box>

            {/* Action chips */}
            <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', justifyContent: 'center' }}>
              {[
                { symbol: 'open_with',  label: 'גרור ושחרר' },
                { symbol: 'zoom_out_map', label: 'שנה גדלים' },
                { symbol: 'settings',   label: 'ערוך הגדרות' },
                { symbol: 'bolt',       label: 'נתונים חיים' },
              ].map(({ symbol, label }) => (
                <Box key={label} sx={{
                  display: 'flex', alignItems: 'center', gap: 0.75,
                  bgcolor: 'rgba(154,114,9,0.12)',
                  border: '1px solid rgba(154,114,9,0.35)',
                  borderRadius: '20px', px: 1.75, py: 0.6,
                }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 15, color: '#d4a017', lineHeight: 1 }}>
                    {symbol}
                  </span>
                  <Typography sx={{
                    color: 'rgba(255,255,255,0.8)', fontFamily: 'Heebo, sans-serif',
                    fontWeight: 600, fontSize: '0.82rem',
                  }}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Preview disclaimer */}
            <Typography sx={{
              color: 'rgba(255,255,255,0.62)',
              fontFamily: 'Heebo, sans-serif',
              fontSize: '0.85rem',
              textAlign: 'center',
              mt: 0.5,
            }}>
              טעימה בלבד — במערכת המלאה עוד רכיבים, תבניות ויכולות
            </Typography>

            {/* Replay onboarding button */}
            <Button
              size="small"
              onClick={() => iframeRef.current?.contentWindow?.postMessage({ type: 'INORAI_SHOW_ONBOARDING' }, '*')}
              sx={{
                mt: 0.5,
                color: 'rgba(255,255,255,0.38)',
                fontFamily: 'Heebo, sans-serif',
                fontSize: '0.78rem',
                textDecoration: 'underline',
                textUnderlineOffset: '3px',
                '&:hover': { color: 'rgba(255,255,255,0.72)', bgcolor: 'transparent' },
              }}
            >
              ▶ הצג הדרכה אינטראקטיבית
            </Button>
          </Box>
        </Box>

        {/* ── TEXT ── */}
        <Box
          ref={textBoxRef}
          sx={{
            position: 'absolute', top: 0, left: 0, right: 0,
            direction: 'rtl',
            pt: { md: '92px', lg: '100px' }, px: { md: '5vw' },
            zIndex: 3, display: { xs: 'none', md: 'flex' },
            flexDirection: 'column', alignItems: 'flex-end',
            pointerEvents: 'auto',
          }}
          style={{ opacity: 1, transform: 'translateY(0px)' }}
        >
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.75,
            bgcolor: 'rgba(154,114,9,0.12)', border: '1px solid rgba(154,114,9,0.35)',
            borderRadius: '20px', px: 1.75, py: 0.5, mb: 2.5,
          }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#9a7209' }} />
            <Typography sx={{ color: '#d4a017', fontFamily: 'Heebo, sans-serif', fontWeight: 600, fontSize: '0.8rem' }}>
              ניהול מסכים לבית הכנסת
            </Typography>
          </Box>

          <Typography component="h1" dir="rtl" sx={{
            fontFamily: 'Rubik, sans-serif', fontWeight: 900, color: 'white',
            fontSize: { md: '5.2rem', lg: '6.5rem' },
            lineHeight: 1.04, letterSpacing: '-0.04em', textAlign: 'right', mb: 1.5,
            direction: 'rtl',
          }}>
            המסך{' '}
            <Box component="span" className="lp-gold-text">החי</Box>
            {' '}של בית הכנסת שלך
          </Typography>

          <Typography sx={{
            color: 'rgba(255,255,255,0.5)', fontFamily: 'Heebo, sans-serif',
            fontSize: '1.1rem', lineHeight: 1.85, maxWidth: '380px', textAlign: 'right', mb: 3.5,
          }}>
            21+ רכיבים ייחודיים. זמני תפילה בזמן אמת.
            ניהול רב-מסכים. עיצוב חופשי לחלוטין.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Button variant="contained" size="large" onClick={() => navigate('/login')}
              sx={{
                bgcolor: '#9a7209', color: 'white',
                fontFamily: 'Heebo, sans-serif', fontWeight: 700, fontSize: '1rem',
                px: 4.5, py: 1.5, borderRadius: 2,
                boxShadow: '0 8px 32px rgba(154,114,9,0.45)',
                '&:hover': { bgcolor: '#b8860b', transform: 'translateY(-2px)', boxShadow: '0 14px 40px rgba(154,114,9,0.55)' },
                transition: 'all 0.25s ease',
              }}
            >
              התחל חינם — 14 יום
            </Button>
            <Button variant="outlined" size="large" onClick={scrollToDemo}
              sx={{
                color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.2)',
                fontFamily: 'Heebo, sans-serif', fontSize: '0.95rem', px: 3.5, py: 1.5, borderRadius: 2,
                '&:hover': { borderColor: 'rgba(255,255,255,0.45)', bgcolor: 'rgba(255,255,255,0.04)' },
              }}
            >
              צפה בהדגמה ↓
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {['14 יום חינם', 'ללא כרטיס אשראי'].map(t => (
              <Box key={t} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box component="span" sx={{ color: '#9a7209', fontWeight: 700, fontSize: '0.7rem' }}>✓</Box>
                <Typography sx={{ color: 'rgba(255,255,255,0.32)', fontSize: '0.78rem', fontFamily: 'Heebo, sans-serif' }}>
                  {t}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Scroll hint */}
        <Box
          ref={scrollHintRef}
          sx={{
            position: 'absolute', bottom: 26, left: '50%', zIndex: 4,
            transform: 'translateX(-50%)', display: { xs: 'none', md: 'flex' },
            flexDirection: 'column', alignItems: 'center', gap: 0.75, pointerEvents: 'none',
          }}
          style={{ opacity: 1 }}
        >
          <Typography sx={{ color: 'rgba(255,255,255,0.26)', fontFamily: 'Heebo, sans-serif', fontSize: '0.7rem' }}>
            גלול למטה
          </Typography>
          <Box sx={{ width: 20, height: 30, borderRadius: 10, border: '1.5px solid rgba(255,255,255,0.15)', display: 'flex', justifyContent: 'center', pt: '5px' }}>
            <Box sx={{ width: 3, height: 6, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.3)' }} />
          </Box>
        </Box>

        {/* ── Mobile ── */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', height: '100%', pt: '88px', px: 3, direction: 'rtl' }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, bgcolor: 'rgba(154,114,9,0.12)', border: '1px solid rgba(154,114,9,0.35)', borderRadius: '20px', px: 1.5, py: 0.4, mb: 2, alignSelf: 'flex-end' }}>
            <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#9a7209' }} />
            <Typography sx={{ color: '#d4a017', fontFamily: 'Heebo, sans-serif', fontWeight: 600, fontSize: '0.75rem' }}>
              ניהול מסכים לבית הכנסת
            </Typography>
          </Box>
          <Typography component="h1" dir="rtl" sx={{ fontFamily: 'Rubik, sans-serif', fontWeight: 900, color: 'white', fontSize: '2.6rem', lineHeight: 1.1, letterSpacing: '-0.03em', textAlign: 'right', mb: 1.5, direction: 'rtl' }}>
            המסך{' '}<Box component="span" className="lp-gold-text">החי</Box>{' '}של בית הכנסת שלך
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Heebo, sans-serif', fontSize: '0.95rem', lineHeight: 1.8, textAlign: 'right', mb: 3 }}>
            21+ רכיבים ייחודיים. זמנים בזמן אמת. עיצוב חופשי.
          </Typography>
          <Button variant="contained" size="large" onClick={() => navigate('/login')} fullWidth
            sx={{ bgcolor: '#9a7209', color: 'white', fontFamily: 'Heebo, sans-serif', fontWeight: 700, fontSize: '1rem', py: 1.5, borderRadius: 2, mb: 1.5, boxShadow: '0 8px 24px rgba(154,114,9,0.4)' }}
          >
            התחל חינם — 14 יום
          </Button>
          <Button variant="outlined" size="large" onClick={scrollToDemo} fullWidth
            sx={{ color: 'rgba(255,255,255,0.65)', borderColor: 'rgba(255,255,255,0.2)', fontFamily: 'Heebo, sans-serif', fontSize: '0.9rem', py: 1.25, borderRadius: 2 }}
          >
            צפה בהדגמה ↓
          </Button>
          <Box sx={{ mt: 3, flex: 1, overflow: 'hidden', borderRadius: 2, bgcolor: '#030a14', p: '8px 8px 0 8px' }}>
            <Box sx={{ display: 'flex', gap: '4px', mb: 1 }}>
              {['#ff5f57', '#febc2e', '#28c840'].map(c => (
                <Box key={c} sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: c, opacity: 0.7 }} />
              ))}
            </Box>
            <Box sx={{ bgcolor: 'white', borderRadius: '6px 6px 0 0', overflow: 'hidden', direction: 'rtl' }}>
              <ZmanimWidget zmanim={dummyZmanimData.zmanim} customTimes={dummyZmanimData.customTimes} loading={false} template="Normal" dispatch={noopDispatch} />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default HeroSection;
